import React, { useEffect, useState } from "react";
import Layout from "./Layout";
import {
    Container,
    Box,
    Typography,
    Button,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Menu,
    MenuItem,
    IconButton,
    CircularProgress,
    Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { getMergeReports } from "../services/API";

function Reportes() {
    const navigate = useNavigate();
    const [reports, setReports] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentReportLinks, setCurrentReportLinks] = useState([]);
    const [currentReportIdForMenu, setCurrentReportIdForMenu] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const accountID = localStorage.getItem("selectedAccountValue");

        const fetchReportes = async () => {
            setLoading(true);
            try {
                const response = await getMergeReports(accountID);
                if (response.success && Array.isArray(response.data)) {
                    const parsedReports = response.data.map(report => ({
                        ...report,
                        links: report.links ? JSON.parse(report.links) : [],
                        status: report.status || "unknown",
                        data_source: report.data_source || "N/A"
                    }));
                    setReports(parsedReports);
                } else {
                    console.error("Failed to fetch reports or data is not an array:", response);
                    setReports([]);
                }
            } catch (error) {
                console.error("Error fetching reports:", error);
                setReports([]);
            } finally {
                setLoading(false);
            }
        };

        if (accountID) {
            fetchReportes();
        } else {
            setLoading(false);
        }
    }, []);

    const handleCreateReport = () => {
        navigate("/mergeReport");
    };

    const handleDeleteReport = (id) => {
        alert("Delete report with ID: " + id);
    };

    const handleDownloadMenuClick = (event, reportId, links) => {
        setAnchorEl(event.currentTarget);
        setCurrentReportIdForMenu(reportId);
        setCurrentReportLinks(links);
    };

    const handleDownloadMenuClose = () => {
        setAnchorEl(null);
        setCurrentReportIdForMenu(null);
        setCurrentReportLinks([]);
    };

    const handleDownloadFile = (reportId, filePath) => {
        const encodedReportId = encodeURIComponent(reportId);
        const encodedFilePath = encodeURIComponent(filePath);
        const API_BASE = process.env.REACT_APP_API_URL || "https://api.jungleclick.online/api";
        const url = API_BASE + "/report/download-report/" + encodedReportId + "filePath=" + encodedFilePath;
        window.open(url, "_blank");
        handleDownloadMenuClose();
    };

    const getStatusChipColor = (status) => {
        switch (status.toLowerCase()) {
            case "pending":
                return "info";
            case "completed":
                return "success";
            case "failed":
                return "error";
            case "no data":
                return "warning";
            default:
                return "default";
        }
    };

    const formatStatusText = (status) => {
        switch (status.toLowerCase()) {
            case "pending":
                return "Pending";
            case "completed":
                return "Completed";
            case "failed":
                return "Failed";
            case "no data":
                return "No Data";
            default:
                return status;
        }
    };


    return (
        <Layout>
            <div>
                <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={3}
                    >
                        <Typography variant="h4" component="h2">
                            Generated Reports
                        </Typography>
                        <Button variant="contained" onClick={handleCreateReport}>
                            Create Report
                        </Button>
                    </Box>

                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                            <CircularProgress />
                            <Typography variant="h6" sx={{ ml: 2 }}>Loading reports...</Typography>
                        </Box>
                    ) : (
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="reports table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Report Name</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Data Source</TableCell>
                                        <TableCell>Start Date</TableCell>
                                        <TableCell>End Date</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No reports available.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        reports.map((report) => (
                                            <TableRow
                                                key={report.id}
                                                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {report.id}
                                                </TableCell>
                                                <TableCell>{report.name}</TableCell>
                                                <TableCell>{report.type}</TableCell>
                                                <TableCell>{report.data_source}</TableCell>
                                                <TableCell>{report.start_date}</TableCell>
                                                <TableCell>{report.end_date}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={formatStatusText(report.status)}
                                                        color={getStatusChipColor(report.status)}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => handleDeleteReport(report.id)}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Delete
                                                    </Button>
                                                    {(report.status === "Completed") ? (
                                                        <>
                                                            <IconButton
                                                                aria-label="download files"
                                                                aria-controls={`download-menu-${report.id}`}
                                                                aria-haspopup="true"
                                                                onClick={(event) => handleDownloadMenuClick(event, report.id, report.links)}
                                                                color="success"
                                                            >
                                                                <DownloadIcon />
                                                                {report.links.length > 1 && <MoreVertIcon />}
                                                            </IconButton>
                                                            <Menu
                                                                id={`download-menu-${report.id}`}
                                                                anchorEl={anchorEl}
                                                                open={Boolean(anchorEl) && currentReportIdForMenu === report.id}
                                                                onClose={handleDownloadMenuClose}
                                                            >
                                                                {currentReportLinks.map((link, index) => {
                                                                    const filename = link.split("/").pop();
                                                                    const match = filename.match(/^(B0[A-Z0-9]{8})/);
                                                                    const displayFileName = match ? match[1] : filename;

                                                                    return (
                                                                        <MenuItem
                                                                            key={index}
                                                                            onClick={() => handleDownloadFile(currentReportIdForMenu, link)}
                                                                        >
                                                                            {displayFileName}
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Menu>
                                                        </>
                                                    ) : (
                                                        <Button
                                                            variant="outlined"
                                                            color="success"
                                                            startIcon={<DownloadIcon />}
                                                            disabled
                                                        >
                                                            Not available
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Container>
            </div>
        </Layout>
    );
}

export default Reportes;
